import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Brand } from '../components/common/Brand';
import { Seo } from '../components/Seo';
import './Landing.css';

// Stable "latest" installer URLs — fixed filenames the release deploy refreshes
// each version, so downloads always serve the current build.
const DL = {
  win: 'https://localareacloud.com/downloads/LACloud_x64-setup.exe',
  mac: 'https://localareacloud.com/downloads/LACloud_mac.zip',
  linuxAppImage: 'https://localareacloud.com/downloads/LACloud_amd64.AppImage',
  linuxDeb: 'https://localareacloud.com/downloads/LACloud_amd64.deb',
};

// Public marketing landing page (Obsidian & Champagne). Styles scoped under .lac.
export const Landing: React.FC = () => {
  const [menuOpen, setMenuOpen] = useState(false);

  // Primary download auto-targets the visitor's OS.
  const [dl, setDl] = useState({ href: DL.win, label: 'Download for Windows' });
  useEffect(() => {
    const ua = navigator.userAgent;
    if (/Macintosh|Mac OS X/i.test(ua)) setDl({ href: DL.mac, label: 'Download for macOS' });
    else if (/Linux/i.test(ua) && !/Android/i.test(ua)) setDl({ href: DL.linuxAppImage, label: 'Download for Linux' });
  }, []);

  // Scroll-reveal for elements with .reveal
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('in');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    document.querySelectorAll('.lac .reveal').forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  const check = (
    <svg viewBox="0 0 24 24"><path d="m5 12 5 5 9-11" /></svg>
  );

  const softwareLd = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Local Area Cloud',
    alternateName: 'L.A.Cloud',
    applicationCategory: 'UtilitiesApplication',
    operatingSystem: 'Windows, macOS, Linux, iOS, Android',
    description:
      'Private device-to-device file sharing, automatic backup, messaging, and secure remote access across your own devices — over your local network or your own VPN. Files move directly between your devices and never touch a third-party cloud.',
    url: 'https://localareacloud.com',
    downloadUrl: 'https://localareacloud.com',
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    publisher: {
      '@type': 'Organization',
      name: 'Chrono Travel LLC',
      url: 'https://localareacloud.com',
    },
  };

  return (
    <div className="lac">
      <Seo
        title="Local Area Cloud — Private Device-to-Device File Sharing"
        description="Share files, back up, and message across your own devices at local-network speed — no third-party cloud. Reach your devices anywhere over your own VPN. Free for up to 2 devices; Pro adds unlimited devices."
        path="/"
        jsonLd={softwareLd}
      />
      <a className="skip" href="#top">Skip to content</a>
      {/* NAV */}
      <div className="nav">
        <div className="wrap nav-in">
          <a className="brand" href="#top"><Brand variant="lockup" height={28} /></a>
          <nav className="nav-links">
            <a href="#how">How it works</a>
            <a href="#features">Features</a>
            <a href="#pricing">Pricing</a>
            <Link to="/whats-new">What's new</Link>
          </nav>
          <div className="nav-cta">
            <Link className="signin" to="/login">Sign in</Link>
            <a className="btn btn-primary" href="#download">Download</a>
          </div>
          <button className="burger" aria-label="Menu" onClick={() => setMenuOpen((v) => !v)}>☰</button>
        </div>
        {menuOpen && (
          <div className="wrap" style={{ paddingBottom: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <a href="#how" onClick={() => setMenuOpen(false)}>How it works</a>
            <a href="#features" onClick={() => setMenuOpen(false)}>Features</a>
            <a href="#pricing" onClick={() => setMenuOpen(false)}>Pricing</a>
            <Link to="/whats-new" onClick={() => setMenuOpen(false)}>What's new</Link>
            <Link to="/login" onClick={() => setMenuOpen(false)}>Sign in</Link>
          </div>
        )}
      </div>

      {/* HERO */}
      <section className="hero" id="top">
        <div className="aurora"><i className="a" /><i className="b" /><i className="c" /></div>
        <div className="wrap hero-grid">
          <div>
            <p className="eyebrow">Your private cloud · device to device</p>
            <h1>Your files. Your devices.<br /><span className="em">Nobody else's cloud.</span></h1>
            <p className="lead">Local Area Cloud moves your photos, videos, and documents straight between your own
              computers and phones — at local-network speed, private by design, and never routed through
              anyone's servers but your own.</p>
            <div className="hero-cta">
              <a className="btn btn-primary" href={dl.href}>↓&nbsp; {dl.label}</a>
              <a className="btn btn-ghost" href="#how">See how it works</a>
            </div>
            <div className="trust">
              <span><i className="dot" /> Windows · macOS · Linux · Android · iPhone</span>
              <span><i className="dot" /> Free for 2 devices</span>
              <span><i className="dot" /> No files ever touch our servers</span>
            </div>
          </div>

          <div className="stage" aria-hidden="true">
            <div className="laptop">
              <div className="screen">
                <div className="win-row"><i /><i /><i /></div>
                <div className="flist">
                  <div className="fi"><span className="ic gold" /><span className="nm" /><span className="sz" /></div>
                  <div className="fi"><span className="ic" /><span className="nm" /><span className="sz" /></div>
                  <div className="fi"><span className="ic" /><span className="nm" /><span className="sz" /></div>
                </div>
              </div>
              <div className="base" />
            </div>
            <div className="beam"><div className="packet" /></div>
            <div className="phone">
              <div className="body">
                <div className="notch" />
                <div className="card">
                  <div className="r" /><div className="r r2" />
                  <div className="ok">RECEIVED ✓</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INSIGHT */}
      <section className="band insight reveal">
        <div className="wrap center" style={{ maxWidth: 820 }}>
          <p className="eyebrow">The problem</p>
          <h2 className="big">The "cloud" is just<br />someone else's computer.</h2>
          <p className="lead" style={{ marginTop: 22 }}>Every upload to a big-tech drive copies your private files onto
            servers you don't own or control. Local Area Cloud flips it: your devices talk directly to each other over
            your own network, so your data never leaves your walls.</p>
        </div>
      </section>

      {/* HOW */}
      <section className="band" id="how">
        <div className="wrap">
          <div className="center reveal" style={{ maxWidth: 660 }}>
            <p className="eyebrow">How it works</p>
            <h2 className="big">Three steps. Then it just works.</h2>
          </div>
          <div className="steps">
            <div className="step reveal"><div className="no">01</div><h3>Install</h3>
              <p>Put LACloud on your Windows PC, Mac, and iPhone. A couple of minutes, no configuration to fuss over.</p></div>
            <div className="step reveal"><div className="no">02</div><h3>They find each other</h3>
              <p>Your devices discover one another automatically on your network, tied privately to your account.</p></div>
            <div className="step reveal"><div className="no">03</div><h3>Send instantly</h3>
              <p>Pick a file, pick a device, send. It travels at full local-network speed — no upload, no waiting.</p></div>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="band" id="features">
        <div className="wrap">
          <div className="center reveal" style={{ maxWidth: 660 }}>
            <p className="eyebrow">Why Local Area Cloud</p>
            <h2 className="big">Built the way it should be.</h2>
          </div>
          <div className="pillars">
            <div className="pillar reveal">
              <div className="ic"><svg className="i" viewBox="0 0 24 24"><path d="M12 3l7 3v5c0 4.5-3 8-7 9-4-1-7-4.5-7-9V6z" /><path d="m9 12 2 2 4-4" /></svg></div>
              <h3>Private by design</h3>
              <p>Transfers run device-to-device on your LAN. We keep a directory so your devices can find each other — never your files.</p>
            </div>
            <div className="pillar reveal">
              <div className="ic"><svg className="i" viewBox="0 0 24 24"><path d="M13 2 4 14h6l-1 8 9-12h-6z" /></svg></div>
              <h3>Local-network speed</h3>
              <p>No cloud round-trip. Move gigabytes as fast as your own network allows — the way it should be.</p>
            </div>
            <div className="pillar reveal">
              <div className="ic"><svg className="i" viewBox="0 0 24 24"><rect x="3" y="4" width="13" height="10" rx="2" /><rect x="14" y="9" width="7" height="11" rx="2" /></svg></div>
              <h3>Every device</h3>
              <p>Windows, macOS, Linux, Android, and iPhone — in sync. Your whole personal fleet under one account.</p>
            </div>
            <div className="pillar reveal">
              <div className="ic"><svg className="i" viewBox="0 0 24 24"><path d="M12 3v18" /><path d="M5 8h14" /><path d="M7 21h10" /><path d="M6 8c0 6 12 6 12 0" /></svg></div>
              <h3>Yours to keep</h3>
              <p>Your files live on your devices. Cancel anytime — nothing of yours vanishes from someone's server.</p>
            </div>
          </div>
        </div>
      </section>

      {/* AUTOMATIC BACKUP */}
      <section className="band">
        <div className="wrap split alt">
          <div className="reveal">
            <p className="eyebrow">Set it and forget it</p>
            <h2 className="big" style={{ fontSize: 'clamp(26px,3.6vw,40px)' }}>Automatic backups,<br />device to device.</h2>
            <p className="lead" style={{ marginTop: 20 }}>Pick a folder and a schedule — Local Area Cloud quietly zips it and sends a
              full-quality copy to another of your own devices, all on its own. Your phone backs up over Wi-Fi while it charges,
              so it simply happens overnight. No cloud middleman, nothing downscaled, every file kept exactly as it is.</p>
          </div>
          <div className="panel reveal" aria-hidden="true">
            <div className="glow" style={{ background: 'radial-gradient(circle,rgba(216,189,130,.28),transparent 70%)' }} />
            <div style={{ position: 'relative' }}>
              <div className="chiprow" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
                <span style={{ fontWeight: 600, color: 'var(--ivory)' }}>Camera Roll → Studio Mac</span>
                <span className="chip" style={{ background: 'rgba(216,189,130,.14)', borderColor: 'var(--champ)', color: 'var(--champ)' }}>Nightly · 2:00&nbsp;AM</span>
              </div>
              <div className="node"><span className="d" /><span className="t">Last backup<small>1,854 photos · 104&nbsp;GB · complete ✓</small></span></div>
              <div className="node"><span className="d" /><span className="t">Next backup<small>Tonight · on Wi-Fi &amp; charging</small></span></div>
            </div>
          </div>
        </div>
      </section>

      {/* CROSS-DEVICE FILES */}
      <section className="band">
        <div className="wrap split">
          <div className="reveal">
            <p className="eyebrow">Every device, one place</p>
            <h2 className="big" style={{ fontSize: 'clamp(26px,3.6vw,40px)' }}>Every device's files,<br />right in your pocket.</h2>
            <p className="lead" style={{ marginTop: 20 }}>Open the Files tab and browse the shared folders on any of your devices —
              your home PC from your phone, your laptop from the couch. Download, upload, or tidy up across your whole fleet,
              with per-folder permissions you set and enforce on each device.</p>
          </div>
          <div className="panel reveal" aria-hidden="true">
            <div className="glow" />
            <div className="chiprow" style={{ marginBottom: 16 }}>
              <span className="chip" style={{ background: 'rgba(216,189,130,.14)', borderColor: 'var(--champ)', color: 'var(--champ)' }}>SKYNET — Home PC</span>
              <span className="chip">Studio Mac</span>
              <span className="chip">iPhone</span>
            </div>
            <div className="node"><span className="d" /><span className="t">Documents / Q3<small>SKYNET · 42 files · read &amp; write</small></span></div>
            <div className="node"><span className="d" /><span className="t">Camera Roll<small>iPhone · 1,854 items · read-only</small></span></div>
            <div className="node"><span className="d" /><span className="t">Projects<small>Studio Mac · 8 folders</small></span></div>
          </div>
        </div>
      </section>

      {/* CROSS-NETWORK / VPN */}
      <section className="band">
        <div className="wrap split alt">
          <div className="reveal">
            <p className="eyebrow">Beyond the home</p>
            <h2 className="big" style={{ fontSize: 'clamp(26px,3.6vw,40px)' }}>Away from home?<br />Reach every device.</h2>
            <p className="lead" style={{ marginTop: 20 }}>LACloud includes a built-in WireGuard client. Save your connections and tap
              to bring your devices onto a secure tunnel to a server <em>you</em> control — your router, your UniFi, your own
              WireGuard box. Reach your files, backups, and messages from anywhere, still with no third-party cloud in the middle.</p>
          </div>
          <div className="panel reveal" aria-hidden="true">
            <div className="glow" />
            <div className="chiprow" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <span style={{ fontWeight: 600, color: 'var(--ivory)' }}>Home tunnel</span>
              <span className="chip" style={{ background: 'rgba(70,198,180,.14)', borderColor: 'var(--teal-bright)', color: 'var(--teal-bright)' }}>WireGuard · connected</span>
            </div>
            <div className="node"><span className="d" /><span className="t">SKYNET — Home PC<small>on VPN · 10.7.0.1</small></span></div>
            <div className="node"><span className="d" /><span className="t">iPhone<small>on VPN · 10.7.0.3</small></span></div>
            <div className="node"><span className="d" /><span className="t">Studio Mac<small>on VPN · 10.7.0.6</small></span></div>
          </div>
        </div>
      </section>

      {/* MESSAGING */}
      <section className="band">
        <div className="wrap split">
          <div className="reveal">
            <p className="eyebrow">Talk, not just transfer</p>
            <h2 className="big" style={{ fontSize: 'clamp(26px,3.6vw,40px)' }}>Message your devices,<br />straight across.</h2>
            <p className="lead" style={{ marginTop: 20 }}>Send a note, a photo, or a file between your own devices — private, direct,
              and never through a server in the middle. It's your whole fleet in one conversation: no contacts to add, no cloud
              inbox, nothing to leak. If it's your device, it's already in the chat.</p>
          </div>
          <div className="panel reveal" aria-hidden="true">
            <div className="glow" style={{ background: 'radial-gradient(circle,rgba(70,198,180,.28),transparent 70%)' }} />
            <div className="chat">
              <div className="msg in"><span>Grabbing the Q3 deck off the Mac now 👍</span></div>
              <div className="msg out"><span>Sent it to your phone — it's in Downloads</span></div>
              <div className="msg in"><span>Perfect. Backing up tonight?</span></div>
              <div className="msg out"><span>Already scheduled ✓</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* SECURITY */}
      <section className="band">
        <div className="wrap split alt">
          <div className="reveal">
            <p className="eyebrow">On your terms</p>
            <h2 className="big" style={{ fontSize: 'clamp(26px,3.6vw,40px)' }}>Nothing lands<br />without a yes.</h2>
            <p className="lead" style={{ marginTop: 20 }}>Only devices signed in to your account can reach each other — there's no public
              inbox and no stranger's link, so nothing arrives from outside your own fleet. You decide what each device will accept,
              so nothing lands on your machines without your say-so.</p>
          </div>
          <div className="panel reveal" style={{ textAlign: 'center' }} aria-hidden="true">
            <div className="glow" style={{ background: 'radial-gradient(circle,rgba(216,189,130,.3),transparent 70%)' }} />
            <div style={{ position: 'relative' }}>
              <p className="eyebrow" style={{ marginBottom: 8 }}>Incoming file</p>
              <h3 style={{ fontSize: 22 }}>Q3-Report.pdf</h3>
              <p style={{ fontSize: 13, color: 'var(--faint)', margin: '6px 0 22px' }}>4.2 MB · from Studio Mac</p>
              <div className="chiprow" style={{ justifyContent: 'center' }}>
                <span className="chip" style={{ borderColor: 'var(--stroke-2)' }}>Decline</span>
                <span className="chip" style={{ background: 'rgba(216,189,130,.14)', borderColor: 'var(--champ)', color: 'var(--champ)' }}>Accept</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="band pricing" id="pricing">
        <div className="wrap">
          <div className="center reveal" style={{ maxWidth: 680 }}>
            <p className="eyebrow">Simple, honest pricing</p>
            <h2 className="big">Free on your network.<br />Pro to reach anywhere.</h2>
          </div>
          <div className="plans">
            {/* FREE */}
            <div className="price-card reveal">
              <p className="eyebrow" style={{ marginBottom: 0 }}>Home</p>
              <div className="price-num"><sup>$</sup>0<span style={{ fontFamily: 'var(--sans)', fontSize: 18, color: 'var(--faint)' }}> forever</span></div>
              <p className="price-sub">Everything you need on your own network</p>
              <ul className="feat">
                <li>{check} 1 account · up to 2 devices</li>
                <li>{check} Windows, macOS, Linux, Android &amp; iPhone</li>
                <li>{check} Direct local-network transfers</li>
                <li>{check} Scheduled automatic backups</li>
                <li>{check} Browse &amp; manage files across devices</li>
                <li>{check} Private device-to-device messaging</li>
                <li>{check} Device-level accept/deny security</li>
                <li>{check} Your files never touch our servers</li>
              </ul>
              <Link className="btn btn-ghost" to="/register" style={{ width: '100%', justifyContent: 'center' }}>Get started free</Link>
            </div>
            {/* PRO */}
            <div className="price-card pro reveal">
              <span className="ribbon">14-DAY FREE TRIAL</span>
              <p className="eyebrow" style={{ marginBottom: 0 }}>Pro</p>
              <div className="price-num"><sup>$</sup>10<span style={{ fontFamily: 'var(--sans)', fontSize: 18, color: 'var(--faint)' }}> / year</span></div>
              <p className="price-sub">or $1.50 / month · for families &amp; power users</p>
              <ul className="feat">
                <li>{check} Everything in Home, plus…</li>
                <li>{check} <b style={{ color: 'var(--ivory)', fontWeight: 600 }}>Unlimited devices</b></li>
                <li>{check} <b style={{ color: 'var(--ivory)', fontWeight: 600 }}>Reach your devices anywhere</b> (VPN)</li>
                <li>{check} Priority transfers &amp; support</li>
              </ul>
              <Link className="btn btn-primary" to="/register" style={{ width: '100%', justifyContent: 'center' }}>Start your free trial</Link>
              <div className="bundles">
                <div className="bundle"><b>$10</b>1 yr</div>
                <div className="bundle"><b>$18</b>2 yr<small>Save $2</small></div>
                <div className="bundle"><b>$25</b>3 yr<small>Save $5</small></div>
                <div className="bundle best"><b>$32</b>4 yr<small>Best value</small></div>
                <div className="bundle"><b>$40</b>5 yr<small>-20%</small></div>
              </div>
            </div>
          </div>
          <p className="center" style={{ fontSize: 12.5, color: 'var(--faint)', marginTop: 22 }}>Lock in your price — multi-year Pro plans save up to 20%.</p>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="final band" id="download">
        <div className="wrap">
          <div className="reveal center" style={{ maxWidth: 640 }}>
            <h2 className="big">Take your cloud home.</h2>
            <p className="lead" style={{ margin: '0 auto 30px' }}>Install Local Area Cloud on your PC and start sending files across your
              own devices in minutes.</p>
            <div className="hero-cta" style={{ justifyContent: 'center' }}>
              <a className="btn btn-primary" href={dl.href}>↓&nbsp; {dl.label}</a>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 22px', justifyContent: 'center', marginTop: 22 }}>
              <a href={DL.win} style={{ opacity: 0.85 }}>Windows</a>
              <a href={DL.mac} style={{ opacity: 0.85 }}>macOS</a>
              <a href={DL.linuxAppImage} style={{ opacity: 0.85 }}>Linux&nbsp;(AppImage)</a>
              <a href={DL.linuxDeb} style={{ opacity: 0.85 }}>Linux&nbsp;(.deb)</a>
            </div>
            <p className="lead" style={{ margin: '16px auto 0', fontSize: '.9rem', opacity: 0.6 }}>Android &amp; iPhone apps coming to the App&nbsp;Store and Google&nbsp;Play soon.</p>
          </div>
        </div>
      </section>

      <footer>
        <div className="wrap foot-cases">
          <span className="foot-cases-h">Popular uses</span>
          <Link to="/airdrop-for-windows">AirDrop for Windows</Link>
          <Link to="/transfer-files-phone-to-pc">Transfer files between phone &amp; PC</Link>
          <Link to="/local-network-file-sharing">Local network file sharing</Link>
          <Link to="/file-sharing-without-the-cloud">File sharing without the cloud</Link>
        </div>
        <div className="wrap foot-in">
          <a className="brand" href="#top"><Brand variant="lockup" height={28} /></a>
          <span style={{ color: 'var(--faint)' }}>© 2026 Chrono Travel LLC · Local Area Cloud. Your files, your network.</span>
          <div className="foot-links">
            <a href="#how">How it works</a><a href="#pricing">Pricing</a>
            <Link to="/whats-new">What's new</Link>
            <Link to="/login">Sign in</Link>
            <Link to="/privacy">Privacy</Link><Link to="/terms">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
